import { useTranslation } from 'react-i18next'
import cn from 'classnames'

import styles from './TrustBand.module.scss'

/**
 * The parent product's own trust bar, at the parent product's own scale.
 *
 * It replaced a strip of four measured numbers. The numbers were true, but on the first screen they
 * argued with the headline for the same attention and left the page making two claims at once —
 * where these logos make none: they vouch, and the headline speaks. The figures still appear further
 * down the page, next to what they describe.
 *
 * The images are Intch's real brand assets, served from `public/` rather than re-cut per surface.
 *
 * Shared, not landing-only: the checkout shows the same bar under the packages, where a buyer is
 * deciding whether to hand over money. The i18n keys stay under `landing.*` because they name the
 * CONTENT, which did not move.
 */
/* HEIGHT CEILING: every badge in public/badges is 150px tall natively, so on a 2x display anything
   over 75px is upscaled past its own resolution and softens. These sizes sit just under that — the
   bar is as large as the real assets allow, not as large as the space would take. Going bigger needs
   bigger files from the platform first. */
const BACKERS = [
  { src: '/badges/altair.webp', alt: 'Altair', height: 64 },
  { src: '/badges/google.webp', alt: 'Google', height: 64 },
] as const

type TrustBandProps = {
  /**
   * Smaller marks and tighter gaps, for a surface where the bar is a footnote rather than the second
   * thing on the page (the checkout). It shrinks the LAYOUT — the heights and the gap — rather than
   * scaling the rendered band: a CSS transform leaves the original width in the flow, which is what
   * wrapped the seal onto a second row inside the checkout's narrower column.
   */
  compact?: boolean
}

export const TrustBand = ({ compact = false }: TrustBandProps) => {
  const { t } = useTranslation()
  // Downscaling is safe at any size; only going ABOVE the native 150px softens the marks.
  const h = (full: number) => (compact ? Math.round(full * 0.62) : full)

  return (
    <div className={cn(styles.root, { [styles.compact as string]: compact })} aria-label={t('landing.trust.label')}>
      {/* The class names below are read only by the phone's grid (TrustBand.module.scss); above it
          every mark keeps the height attribute it has always had. */}
      <img className={styles.caption} src="/badges/backed-by.webp" alt={t('landing.hero.backedBy')} height={h(60)} />
      {BACKERS.map((logo) => (
        <img key={logo.alt} className={styles.backer} src={logo.src} alt={logo.alt} height={h(logo.height)} />
      ))}

      {/* Two kinds of proof, kept apart: who backs the company, and what the product has won. */}
      <span className={styles.divider} aria-hidden />

      <img className={styles.award} src="/badges/product-hunt.webp" alt={t('landing.hero.productHunt')} height={h(70)} />
      <img className={styles.seal} src="/badges/hr-awards.webp" alt={t('landing.hero.hrAwards')} height={h(75)} />
    </div>
  )
}
